import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Tooltip } from "@mui/material";
import { CustomLoading, CustomTruncateText } from "@ippon/shared-components";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

/**
 * 予実テーブルの描画コンポーネント (純描画・ロジックなし)。
 * 親 (Budget/index.tsx) が既存ヘルパーで RowVM[] を構築し、表示文字列・編集可否・競合・色を
 * すべて precompute して渡す。縦方向はページのスクロール位置を基準に仮想化し、
 * 横スクロールだけを右ペインに持たせる。左右ペインは常に同じ行範囲を描画するため、
 * 内部の縦スクロール同期による行ずれを起こさない。
 */

export type BudgetField = "budget" | "actual";
export type RowKind =
  | "group"
  | "subgroup"
  | "category"
  | "detail"
  | "addDetail"
  | "addCategory"
  | "otherIncome"
  | "derived";

export interface RowVMCell {
  budgetText: string;
  actualText: string;
  diffText: string;
  diffColor: string;
  budgetColor?: string;
  actualColor?: string;
  editableBudget: boolean;
  editableActual: boolean;
  conflictBudget: boolean;
  conflictActual: boolean;
  // 内訳の実績セルで入金(積み上げ)が存在する場合 true。手動値の変更を
  // インライン編集ではなく必ずモーダル経由にする (競合が無くてもクリックでモーダル)。
  gateActual?: boolean;
  // 科目セルで積み上げ(明細合計)が存在する場合 true (budget/actual それぞれ)。
  gateBudget?: boolean;
}

export type LinkAction = {
  type: "section" | "expense" | "payment";
  id: string;
};
export type AddAction =
  | { kind: "incomeUnit"; modalKind: "private_area" | "ancillary" }
  | { kind: "incomePayment" }
  | {
      kind: "expense";
      rowCategory: string;
      rowCategoryId?: string;
      rowGroup: string;
    }
  | {
      kind: "addCategory";
      categoryType: "income" | "expense_opex" | "expense_capex";
      revenueGroup?: string;
      expenseGroup?: string;
      addKey: string;
    }
  | { kind: "addExpenseCategory" };

export interface RowVM {
  key: string;
  kind: RowKind;
  pl: number; // 左インデント(px)
  bold: boolean;
  label: string;
  labelColor?: string;
  link?: boolean; // ラベルがリンク(下線・クリック可)
  groupBg: boolean; // true=#FAFAFA, false=値行(縞)
  derivedBg?: string; // NOI 等の固有背景
  expandable: boolean;
  expanded: boolean;
  cells: RowVMCell[]; // 長さ = columns.length
  sortKey?: string;
  detailId?: string;
  detailLabel?: string; // 内訳競合モーダル用
  // ↓ 親 (Budget) がコールバックで参照 (この描画コンポーネントは使わない)
  toggleKey?: string;
  linkAction?: LinkAction;
  addAction?: AddAction;
  paymentRaw?: unknown; // 「その他」入金行: 生 PaymentRecord (詳細ドロワー用)
}

export interface BudgetTableVirtualProps {
  rowsVM: RowVM[];
  columns: string[]; // 月 or 四半期ラベル
  loading?: boolean;
  stickyColWidth: number;
  onResizeSticky: (w: number) => void;
  // セル編集
  editingCellKey: string | null;
  editingValue: string;
  onStartEdit: (vm: RowVM, colIdx: number, field: BudgetField) => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  onChangeEditValue: (v: string) => void;
  // 操作
  onToggle: (vm: RowVM) => void;
  onOpenConflict: (vm: RowVM, colIdx: number, field: BudgetField) => void;
  onLabelClick: (vm: RowVM) => void; // リンク or 追加行
  // レイアウト
  subColWidth?: number;
  minStickyWidth?: number;
  maxStickyWidth?: number;
}

const ROW_H = 38;
const HEAD_TOP_H = 32;
const HEAD_SUB_H = 24;
const HEADER_H = HEAD_TOP_H + HEAD_SUB_H;
const OVERSCAN = 8;

const C = {
  group: "#FAFAFA",
  zebra: "rgba(0,0,0,0.02)",
  head: "#FAFAFA",
  border: "#e0e0e0",
  borderLite: "#E5E5E5",
  text: "#3E3E3E",
  sub: "#666",
  gray: "#999",
  faint: "#bbb",
  edit: "#EDF7FA",
  link: "#0B9DBD",
  warn: "#FF9800",
  hover: "rgba(0,0,0,0.04)",
};

// Tooltip をセルに近づける (placement=top で上に離れすぎないように)
const TIP_SLOT_PROPS = {
  popper: {
    modifiers: [{ name: "offset", options: { offset: [0, -8] } }],
  },
};

// 縦スクロールの主体を特定する。予実テーブルはタブによって window ではなく
// 内側の overflowY:auto コンテナ (PropertyDetail の TabPanel 等) の中で
// スクロールするため、その要素の scroll も監視しないと可視範囲が更新されず、
// スクロール後に行が描画されない巨大な余白が出る。最近傍のスクロール可能な
// 祖先を返す (無ければ null = window スクロール)。
const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let el: HTMLElement | null = node?.parentElement ?? null;
  while (el) {
    const oy = window.getComputedStyle(el).overflowY;
    // 展開前でまだオーバーフローしていなくても、スクロール担当の祖先には
    // 先にリスナーを張っておく (オーバーフローした瞬間から scroll を拾える)。
    if (oy === "auto" || oy === "scroll" || oy === "overlay") {
      return el;
    }
    el = el.parentElement;
  }
  return null;
};

// 親 (Budget) の makeCellKey と一致させること
const cellKeyOf = (vm: RowVM, colIdx: number, field: BudgetField): string =>
  vm.detailId
    ? `${vm.sortKey}__d__${vm.detailId}__${colIdx}__${field}`
    : `${vm.sortKey}__${colIdx}__${field}`;

// 元の TruncatedWithTooltip 相当: JS で省略 (CSS の textOverflow:ellipsis は使わない)。
// 省略された時だけ Tooltip を出す。
const TruncatedLabel: React.FC<{
  text: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
}> = ({ text, className, style, onClick }) => {
  const [truncated, setTruncated] = useState(false);
  const inner = (
    <div
      className={className}
      style={{ minWidth: 0, ...style }}
      onClick={onClick}
    >
      <CustomTruncateText text={text} onTruncated={setTruncated} />
    </div>
  );
  return truncated ? (
    <Tooltip title={text} placement="top" arrow slotProps={TIP_SLOT_PROPS}>
      {inner}
    </Tooltip>
  ) : (
    inner
  );
};

const BudgetTableVirtual: React.FC<BudgetTableVirtualProps> = ({
  rowsVM,
  columns,
  loading = false,
  stickyColWidth,
  onResizeSticky,
  editingCellKey,
  editingValue,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onChangeEditValue,
  onToggle,
  onOpenConflict,
  onLabelClick,
  subColWidth = 84,
  minStickyWidth = 110,
  maxStickyWidth = 420,
}) => {
  // 各月ごとに、その月の最長値に合わせてサブ列幅を決める (予算/実績/差異 とも同幅=均等)。
  // 月単位なので 1 つの巨大な値で全列が広がらない。最低幅は維持し、nowrap で折返し防止。
  const monthSubW = useMemo(() => {
    const chars = columns.map(() => 0);
    for (const r of rowsVM)
      for (let ci = 0; ci < r.cells.length; ci++) {
        const c = r.cells[ci];
        chars[ci] = Math.max(
          chars[ci],
          c.budgetText.length,
          c.actualText.length,
          c.diffText.length,
        );
      }
    return chars.map((n) => Math.max(subColWidth, Math.ceil(n * 8) + 6));
  }, [rowsVM, columns, subColWidth]);
  const rightWidth = monthSubW.reduce((s, w) => s + w * 3, 0);
  const total = rowsVM.length;
  const totalH = total * ROW_H;
  const bodyH = loading && total === 0 ? 240 : totalH + 6;

  const [showL, setShowL] = useState(false);
  const [showR, setShowR] = useState(true);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 });

  const tableRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const scrollRafRef = useRef(0);
  // 下部の横スクロールバー帯(6px filler)を最下行と同色で塗り、色割れを防ぐ。
  // NOI/合計など固有背景は derivedBg、追加行は #FAFAFA、値行は左#FAFAFA/右#fff。
  const lastVM = rowsVM[total - 1];
  const lastIsAdd =
    lastVM?.kind === "addDetail" || lastVM?.kind === "addCategory";
  const fillerLeftBg = lastVM?.derivedBg ?? "#FAFAFA";
  const fillerRightBg = lastVM?.derivedBg ?? (lastIsAdd ? C.group : "#fff");

  useEffect(() => {
    if (editingCellKey) setTimeout(() => editInputRef.current?.select(), 0);
  }, [editingCellKey]);

  const updateVisibleRange = useCallback(() => {
    const table = tableRef.current;
    if (!table) return;

    const bodyTop = table.getBoundingClientRect().top + HEADER_H;
    const visibleTop = Math.max(0, Math.min(totalH, -bodyTop));
    const visibleBottom = Math.max(
      visibleTop,
      Math.min(totalH, window.innerHeight - bodyTop),
    );
    const start = Math.max(0, Math.floor(visibleTop / ROW_H) - OVERSCAN);
    const end = Math.min(total, Math.ceil(visibleBottom / ROW_H) + OVERSCAN);

    setVisibleRange((current) =>
      current.start === start && current.end === end ? current : { start, end },
    );
  }, [total, totalH]);

  useEffect(() => {
    const onScroll = () => {
      if (scrollRafRef.current) return;
      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = 0;
        updateVisibleRange();
      });
    };

    updateVisibleRange();
    // 実スクロール主体 (内側 overflow コンテナ or window) を監視する。
    // window だけだと内部スクロール時に可視範囲が凍結して余白が出る。
    const scrollParent = getScrollParent(tableRef.current);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    if (scrollParent) {
      scrollParent.addEventListener("scroll", onScroll, { passive: true });
    }
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (scrollParent) {
        scrollParent.removeEventListener("scroll", onScroll);
      }
      window.cancelAnimationFrame(scrollRafRef.current);
    };
  }, [updateVisibleRange]);

  const windowRows = rowsVM.slice(visibleRange.start, visibleRange.end);

  const onRightScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (headRef.current) headRef.current.scrollLeft = el.scrollLeft;
    setShowL(el.scrollLeft > 0);
    setShowR(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  const onResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = stickyColWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const move = (ev: MouseEvent) =>
      onResizeSticky(
        Math.min(
          maxStickyWidth,
          Math.max(minStickyWidth, startW + (ev.clientX - startX)),
        ),
      );
    const up = () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  // ---- 左ラベル行 ----
  const renderLeftRow = (vm: RowVM, index: number) => {
    const top = index * ROW_H;
    const base: React.CSSProperties = {
      position: "absolute",
      top,
      left: 0,
      right: 0,
      height: ROW_H,
      display: "flex",
      alignItems: "center",
      boxSizing: "border-box",
      paddingLeft: vm.pl,
      paddingRight: 8,
      fontSize: 14,
      borderBottom: `1px solid ${C.border}`,
      background: vm.derivedBg ?? "#FAFAFA",
      color: vm.labelColor ?? C.text,
      fontWeight: vm.bold ? 700 : 400,
      whiteSpace: "nowrap",
      overflow: "hidden",
    };
    if (vm.kind === "addDetail" || vm.kind === "addCategory") {
      return (
        <div
          key={vm.key}
          style={{
            ...base,
            color: C.gray,
            cursor: "pointer",
            background: C.group,
          }}
          onClick={() => onLabelClick(vm)}
          onMouseEnter={(e) => (e.currentTarget.style.color = C.link)}
          onMouseLeave={(e) => (e.currentTarget.style.color = C.gray)}
        >
          <AddIcon style={{ fontSize: 16, marginRight: 4 }} />
          <span style={{ fontSize: 13 }}>{vm.label}</span>
        </div>
      );
    }
    return (
      <div
        key={vm.key}
        style={{ ...base, cursor: vm.expandable ? "pointer" : "default" }}
        onClick={vm.expandable ? () => onToggle(vm) : undefined}
      >
        {vm.expandable ? (
          <span
            className="bv-chevron"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 2,
              flexShrink: 0,
              borderRadius: "50%",
              padding: 1,
            }}
          >
            <KeyboardArrowRightIcon
              style={{
                fontSize: 18,
                transition: "transform 0.2s",
                transform: vm.expanded ? "rotate(90deg)" : "none",
                color: vm.expanded ? C.link : C.text,
              }}
            />
          </span>
        ) : vm.kind === "derived" ? null : (
          <span style={{ width: 18, flexShrink: 0 }} />
        )}
        <TruncatedLabel
          text={vm.label}
          className={vm.link ? "bv-link" : undefined}
          onClick={
            vm.link
              ? (e) => {
                  e.stopPropagation();
                  onLabelClick(vm);
                }
              : undefined
          }
          style={{
            flex: 1,
            cursor: vm.link ? "pointer" : "default",
            // 通常色＋下線。ホバーで青 (CSS)。本番準拠。
            textDecoration: vm.link ? "underline" : "none",
          }}
        />
      </div>
    );
  };

  // ---- 右データ行 ----
  const renderRightRow = (vm: RowVM, index: number) => {
    const top = index * ROW_H;
    if (vm.kind === "addDetail" || vm.kind === "addCategory")
      return (
        <div
          key={vm.key}
          style={{
            position: "absolute",
            top,
            left: 0,
            width: rightWidth,
            height: ROW_H,
            background: C.group,
            borderBottom: `1px solid ${C.border}`,
            boxSizing: "border-box",
          }}
        />
      );

    return (
      <div
        key={vm.key}
        style={{
          position: "absolute",
          top,
          left: 0,
          width: rightWidth,
          height: ROW_H,
          display: "flex",
          boxSizing: "border-box",
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        {columns.map((_, ci) => {
          const cell = vm.cells[ci];
          const w = monthSubW[ci];
          // 本番準拠: 全行とも月列のゼブラ (奇数列のみ淡灰)。NOI 等は固有背景。
          const bg = vm.derivedBg ?? (ci % 2 === 1 ? C.zebra : undefined);
          const cellBase: React.CSSProperties = {
            width: w,
            height: ROW_H,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingRight: 2,
            paddingLeft: 2,
            boxSizing: "border-box",
            fontSize: 14,
            fontVariantNumeric: "tabular-nums",
            fontWeight: vm.bold ? 700 : 400,
            background: bg,
            overflow: "hidden",
            whiteSpace: "nowrap",
            // 下線は inset box-shadow で背景の上に描く (青背景の編集中でも消えない)。
            boxShadow: `inset 0 -1px 0 ${C.border}`,
          };
          const valCell = (
            field: BudgetField,
            text: string,
            editable: boolean,
            conflict: boolean,
            color: string,
            gate?: boolean,
          ) => {
            const key = cellKeyOf(vm, ci, field);
            const isEditing = editingCellKey === key;
            // 入金(積み上げ)がある内訳実績セルは、競合が無くても手動変更をモーダル経由にする。
            if (gate && !conflict && editable && !isEditing) {
              return (
                <Tooltip
                  title="クリックで入力 (入金と選択)"
                  placement="top"
                  arrow
                  slotProps={TIP_SLOT_PROPS}
                >
                  <div
                    className="bv-editcell"
                    style={{
                      ...cellBase,
                      cursor: "pointer",
                      position: "relative",
                      color,
                    }}
                    onClick={() => onOpenConflict(vm, ci, field)}
                  >
                    {text}
                    <EditOutlinedIcon
                      className="edit-icon-overlay"
                      style={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        fontSize: 13,
                        color: C.sub,
                        opacity: 0,
                        transition: "opacity 0.15s",
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                </Tooltip>
              );
            }
            if (conflict) {
              // 青背景は付けない (⚠️ アイコンだけで示す)。通常のゼブラ背景のまま。
              return (
                <Tooltip
                  title="クリックで選択"
                  placement="top"
                  arrow
                  slotProps={TIP_SLOT_PROPS}
                >
                  <div
                    className="bv-editcell"
                    style={{
                      ...cellBase,
                      cursor: "pointer",
                      position: "relative",
                      color,
                    }}
                    onClick={() => onOpenConflict(vm, ci, field)}
                  >
                    {text}
                    <WarningAmberIcon
                      style={{
                        fontSize: 13,
                        color: C.warn,
                        position: "absolute",
                        top: 1,
                        right: 1,
                      }}
                    />
                  </div>
                </Tooltip>
              );
            }
            if (isEditing) {
              return (
                <div style={{ ...cellBase, background: C.edit }}>
                  <input
                    ref={editInputRef}
                    value={editingValue}
                    onChange={(e) => onChangeEditValue(e.target.value)}
                    onBlur={onCommitEdit}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onCommitEdit();
                      else if (e.key === "Escape") onCancelEdit();
                    }}
                    style={{
                      width: "100%",
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      textAlign: "center",
                      fontSize: 14,
                      fontVariantNumeric: "tabular-nums",
                      color: C.text,
                    }}
                  />
                </div>
              );
            }
            const cellNode = (
              <div
                className={editable ? "bv-editcell" : undefined}
                style={{
                  ...cellBase,
                  color,
                  cursor: editable ? "pointer" : "default",
                  position: "relative",
                }}
                onClick={
                  editable ? () => onStartEdit(vm, ci, field) : undefined
                }
              >
                {text}
                {editable && (
                  <EditOutlinedIcon
                    className="edit-icon-overlay"
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      fontSize: 13,
                      color: C.sub,
                      opacity: 0,
                      transition: "opacity 0.15s",
                      pointerEvents: "none",
                    }}
                  />
                )}
              </div>
            );
            return editable ? (
              <Tooltip
                title="クリックで編集"
                placement="top"
                arrow
                slotProps={TIP_SLOT_PROPS}
              >
                {cellNode}
              </Tooltip>
            ) : (
              cellNode
            );
          };
          // 値の文字色 (行種別)
          const isDetail = vm.kind === "detail";
          const budgetColor =
            cell.budgetColor ?? (isDetail ? C.faint : vm.bold ? C.sub : C.text);
          const actualColor =
            cell.actualColor ?? (isDetail ? C.sub : vm.bold ? C.sub : C.text);
          return (
            <div key={ci} style={{ display: "flex", flexShrink: 0 }}>
              {valCell(
                "budget",
                cell.budgetText,
                cell.editableBudget,
                cell.conflictBudget,
                budgetColor,
                cell.gateBudget,
              )}
              {valCell(
                "actual",
                cell.actualText,
                cell.editableActual,
                cell.conflictActual,
                actualColor,
                cell.gateActual,
              )}
              <div style={{ ...cellBase, color: cell.diffColor }}>
                {cell.diffText}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const monthHead = (label: string, ci: number) => (
    <div
      key={ci}
      style={{ width: monthSubW[ci] * 3, flexShrink: 0, background: "#fff" }}
    >
      <div
        style={{
          height: HEAD_TOP_H,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          color: C.text,
          borderBottom: `1px solid ${C.border}`,
          background: ci % 2 === 1 ? C.zebra : undefined,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", height: HEAD_SUB_H }}>
        {["予算", "実績", "差異"].map((s) => (
          <div
            key={s}
            style={{
              width: monthSubW[ci],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              color: C.sub,
              borderBottom: `1px solid ${C.border}`,
              background: ci % 2 === 1 ? C.zebra : undefined,
            }}
          >
            {s}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Box
      ref={tableRef}
      sx={{
        display: "flex",
        border: `1px solid ${C.border}`,
        borderRadius: "8px",
        overflow: "hidden",
        background: "#fff",
        position: "relative",
      }}
    >
      <style>{`.bv-editcell:hover{background:rgba(0,0,0,0.04)!important;}.bv-editcell:hover .edit-icon-overlay{opacity:1!important;}.bv-link:hover{color:#0B9DBD!important;}.bv-chevron:hover{background:rgba(0,0,0,0.04);}`}</style>
      {/* ===== 左ペイン ===== */}
      <Box
        sx={{
          width: stickyColWidth,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            height: HEADER_H,
            display: "flex",
            alignItems: "center",
            padding: "0 8px 0 16px",
            fontSize: 13,
            fontWeight: 700,
            color: C.text,
            background: C.head,
            borderBottom: `1px solid ${C.border}`,
            boxSizing: "border-box",
          }}
        >
          科目
        </div>
        <Box sx={{ height: bodyH, overflow: "hidden", background: "#FAFAFA" }}>
          {/* 右ペインの横スクロールバー分の余白を確保し、最終行の高さを左右で揃える。 */}
          <div style={{ position: "relative", height: bodyH }}>
            {windowRows.map((vm, i) =>
              renderLeftRow(vm, visibleRange.start + i),
            )}
            {/* 横スクロールバー帯を最下行と同色で塗り色割れを防ぐ (最下行の下線も覆う) */}
            {total > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: totalH - 1,
                  left: 0,
                  right: 0,
                  height: 7,
                  background: fillerLeftBg,
                }}
              />
            )}
          </div>
        </Box>
      </Box>

      {/* ===== リサイズハンドル (ホバー時のみ細い薄グレー線) ===== */}
      <Box sx={{ position: "relative", width: 0, flexShrink: 0, zIndex: 5 }}>
        <Box
          onMouseDown={onResizeStart}
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "-4px",
            width: "9px",
            cursor: "col-resize",
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              left: "4px",
              width: "1px",
              background: "#C9C9C9",
              opacity: 0,
              transition: "opacity 0.15s ease",
              pointerEvents: "none",
            },
            "&:hover::after": { opacity: 1 },
          }}
        />
      </Box>

      {/* ===== 右ペイン ===== */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Box
          ref={headRef}
          sx={{
            height: HEADER_H,
            boxSizing: "border-box",
            overflow: "hidden",
            flexShrink: 0,
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div style={{ display: "flex", width: rightWidth }}>
            {columns.map((m, ci) => monthHead(m, ci))}
          </div>
        </Box>
        <Box
          onScroll={onRightScroll}
          sx={{ height: bodyH, overflowX: "auto", overflowY: "hidden" }}
        >
          {/* 横スクロールだけをこのペインに持たせる。縦方向はページ本体でスクロールする。 */}
          <div
            style={{
              position: "relative",
              width: rightWidth,
              height: bodyH,
            }}
          >
            {windowRows.map((vm, i) =>
              renderRightRow(vm, visibleRange.start + i),
            )}
            {/* 横スクロールバー帯を最下行と同色で塗り色割れを防ぐ (最下行の下線も覆う) */}
            {total > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: totalH - 1,
                  left: 0,
                  width: rightWidth,
                  height: 7,
                  background: fillerRightBg,
                }}
              />
            )}
          </div>
        </Box>
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 12,
            pointerEvents: "none",
            zIndex: 3,
            background:
              "linear-gradient(to right, rgba(0,0,0,0.12), rgba(0,0,0,0))",
            opacity: showL ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
        />
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 12,
            pointerEvents: "none",
            zIndex: 3,
            background:
              "linear-gradient(to left, rgba(0,0,0,0.12), rgba(0,0,0,0))",
            opacity: showR ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
        />
      </Box>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: HEADER_H,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
          }}
        >
          <CustomLoading loading={true} fullScreen={false} size="medium" />
        </Box>
      )}
    </Box>
  );
};

export default BudgetTableVirtual;
